import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { motion } from "framer-motion";
import { Shield, ShieldPlus, UserPlus, Trash2, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";

export default function ManagerAdmins() {
  const qc = useQueryClient();
  const [linkEmail, setLinkEmail] = useState("");
  const [linking, setLinking] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [creating, setCreating] = useState(false);
  const [revoke, setRevoke] = useState<{ user_id: string; email: string } | null>(null);

  const { data: admins = [], isLoading } = useQuery({
    queryKey: ["manager_admins"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("admin-stats", {
        body: { section: "list_admins" },
      });
      if (error) throw error;
      return data?.admins || [];
    },
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["manager_admins"] });

  const grantByEmail = async () => {
    if (!linkEmail.trim()) return;
    setLinking(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-stats", {
        body: { section: "grant_admin_by_email", email: linkEmail.trim() },
      });
      if (error || data?.error) throw new Error(data?.error || error?.message);
      toast.success(`${data.email} agora é admin`);
      setLinkEmail("");
      refresh();
    } catch (e: any) {
      toast.error(e.message || "Erro ao vincular");
    } finally {
      setLinking(false);
    }
  };

  const createAdmin = async () => {
    if (!newEmail.trim() || newPwd.length < 6) {
      toast.error("E-mail válido e senha de 6+ caracteres");
      return;
    }
    setCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-stats", {
        body: { section: "create_admin", email: newEmail.trim(), password: newPwd },
      });
      if (error || data?.error) throw new Error(data?.error || error?.message);
      toast.success("Admin criado com sucesso");
      setNewEmail(""); setNewPwd(""); setCreateOpen(false);
      refresh();
    } catch (e: any) {
      toast.error(e.message || "Erro ao criar admin");
    } finally {
      setCreating(false);
    }
  };

  const revokeAdmin = async () => {
    if (!revoke) return;
    try {
      const { data, error } = await supabase.functions.invoke("admin-stats", {
        body: { section: "revoke_admin", user_id: revoke.user_id },
      });
      if (error || data?.error) throw new Error(data?.error || error?.message);
      toast.success("Acesso admin revogado");
      setRevoke(null);
      refresh();
    } catch (e: any) {
      toast.error(e.message || "Erro ao revogar");
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground">Administradores</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Gerencie quem tem acesso ao painel <strong>/manager</strong>.
        </p>
      </div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <Tabs defaultValue="link">
          <TabsList>
            <TabsTrigger value="link"><ShieldPlus className="h-3.5 w-3.5 mr-1.5" /> Vincular existente</TabsTrigger>
            <TabsTrigger value="create"><UserPlus className="h-3.5 w-3.5 mr-1.5" /> Criar novo</TabsTrigger>
          </TabsList>

          <TabsContent value="link" className="mt-4">
            <Card className="glass-card border-warmz-glass-border/50">
              <CardContent className="p-5 space-y-4">
                <p className="text-sm text-muted-foreground">
                  Promova um usuário já existente a administrador informando o e-mail dele.
                </p>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="email"
                      placeholder="usuario@dominio.com"
                      value={linkEmail}
                      onChange={(e) => setLinkEmail(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Button onClick={grantByEmail} disabled={linking || !linkEmail.trim()}>
                    {linking && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Promover a admin
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="create" className="mt-4">
            <Card className="glass-card border-warmz-glass-border/50">
              <CardContent className="p-5 space-y-4">
                <p className="text-sm text-muted-foreground">
                  Crie uma nova conta já com acesso administrativo. O e-mail é confirmado automaticamente.
                </p>
                <Button onClick={() => setCreateOpen(true)}>
                  <UserPlus className="h-4 w-4 mr-2" /> Criar novo admin
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <Card className="glass-card border-warmz-glass-border/50">
          <CardContent className="p-0">
            <div className="p-4 border-b border-warmz-glass-border/30 flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              <h2 className="font-display text-lg font-semibold">Admins atuais ({admins.length})</h2>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-warmz-glass-border/30">
                    <TableHead>E-mail</TableHead>
                    <TableHead>User ID</TableHead>
                    <TableHead>Concedido em</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 4 }).map((_, j) => (
                          <TableCell key={j}><Skeleton className="h-5 w-32" /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : admins.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                        Nenhum admin cadastrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    admins.map((a: any) => (
                      <TableRow key={a.id}>
                        <TableCell className="text-sm font-medium">{a.email}</TableCell>
                        <TableCell><span className="font-mono text-xs text-muted-foreground">{a.user_id}</span></TableCell>
                        <TableCell><span className="text-xs text-muted-foreground">{new Date(a.created_at).toLocaleDateString("pt-BR")}</span></TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setRevoke({ user_id: a.user_id, email: a.email })}
                            title="Revogar admin"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Create new admin dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Criar novo administrador</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>E-mail</Label>
              <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="admin@dominio.com" />
            </div>
            <div className="space-y-1.5">
              <Label>Senha temporária</Label>
              <Input type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} placeholder="Mín. 6 caracteres" />
              <p className="text-xs text-muted-foreground">Compartilhe a senha de forma segura com o novo admin.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={createAdmin} disabled={creating}>
              {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Criar admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke confirm */}
      <AlertDialog open={!!revoke} onOpenChange={(v) => !v && setRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revogar acesso admin</AlertDialogTitle>
            <AlertDialogDescription>
              Remover acesso administrativo de <strong>{revoke?.email}</strong>? A conta do usuário continua existindo, apenas perde o acesso ao /manager.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={revokeAdmin} className="bg-destructive hover:bg-destructive/90">
              Revogar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
